import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CreditCard, Rocket, Lock, Database, LogOut, ChevronRight, Wrench, LifeBuoy, CalendarCheck, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";

const BUSINESS = [
  { icon: Briefcase, label: "Business Settings", to: "/business-settings", testId: "row-business" },
  { icon: FileText, label: "Invoice Design & Branding", to: "/invoice-design", testId: "row-invoice-design" },
  { icon: CalendarCheck, label: "Online Booking", to: "/online-booking", testId: "row-online-booking" },
  { icon: Rocket, label: "Garage Setup", to: "/onboarding", testId: "row-onboarding" },
  { icon: CreditCard, label: "Subscription & Billing", to: "/billing", testId: "row-billing" },
];
const PRIVACY = [
  { icon: Lock, label: "Privacy Policy", to: "/privacy", testId: "row-privacy" },
  { icon: Database, label: "Customer Data & GDPR", to: "/gdpr", testId: "row-gdpr" },
];

export default function More() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [garage, setGarage] = useState(null);
  useEffect(() => { api("/garage/settings").then(setGarage).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Account & Settings" subtitle="Business settings, subscription and account management" />

      <div className="mb-6 flex items-center gap-4 rounded-xl border border-border bg-card p-5" data-testid="garage-card">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-tertiary"><Wrench className="h-6 w-6 text-brand" /></div>
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-midnight" data-testid="account-garage-name">{garage?.name || user?.name || "Your Garage"}</p>
          <p className="truncate text-xs text-muted-foreground" data-testid="account-email">{user?.email}</p>
          {garage?.vat_number && <p className="text-xs font-bold text-brand-2" data-testid="account-vat">VAT: {garage.vat_number}</p>}
        </div>
      </div>

      <Section title="Business Settings" rows={BUSINESS} navigate={navigate} />
      <Section title="Privacy & Data" rows={PRIVACY} navigate={navigate} />

      <p className="mb-2 mt-6 text-[11px] font-extrabold tracking-widest text-muted-foreground">HELP & SUPPORT</p>
      <a href="mailto:support@progarages.co.uk" data-testid="row-support" className="mb-2 flex items-center gap-3 rounded-xl border border-border bg-card p-4 hover:bg-secondary">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tertiary text-brand"><LifeBuoy className="h-4 w-4" /></div>
        <span className="flex-1 text-sm font-semibold text-midnight">Help & Support</span><ChevronRight className="h-4 w-4 text-muted-foreground" />
      </a>

      <p className="mb-2 mt-6 text-[11px] font-extrabold tracking-widest text-muted-foreground">ACCOUNT</p>
      <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="row-logout" className="flex w-full items-center gap-3 rounded-xl border border-danger/30 bg-danger-bg p-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white"><LogOut className="h-4 w-4 text-danger" /></div>
        <span className="flex-1 text-left text-sm font-semibold text-danger">Sign Out</span>
      </button>
      <p className="mt-8 text-center text-[11px] text-muted-foreground">Pro Garages · v4.0 · UK GDPR compliant</p>
    </div>
  );
}

function Section({ title, rows, navigate }) {
  return (
    <>
      <p className="mb-2 mt-4 text-[11px] font-extrabold tracking-widest text-muted-foreground">{title.toUpperCase()}</p>
      <div className="space-y-2">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <button key={r.to} onClick={() => navigate(r.to)} data-testid={r.testId} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 hover:bg-secondary">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-tertiary text-brand"><Icon className="h-4 w-4" /></div>
              <span className="flex-1 text-left text-sm font-semibold text-midnight">{r.label}</span><ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>
    </>
  );
}
